import { useCallback, useState, useEffect, useRef } from 'react'
import { promptReload } from '@/lib/promptReload'
import { AGENT_URL, APP_VERSION, ENV } from '../config/env'

interface VersionCheckConfig {
  /** Check interval in milliseconds (default: 30 minutes) */
  checkInterval?: number
  /** Enable automatic checking (default: true) */
  enabled?: boolean
}

export const useVersionCheck = (config: VersionCheckConfig = {}) => {
  // Check every 30 minutes
  const { checkInterval = 30 * 60 * 1000, enabled = true } = config
  const [newVersionAvailable, setNewVersionAvailable] = useState(false)
  const [currentVersion, setCurrentVersion] = useState<string | null>(null)
  const [latestVersion, setLatestVersion] = useState<string | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Get build-time version from centralized environment config

  // Stable across renders so the effect below can declare it as a dependency
  // without re-arming the interval on every render. `enabled` is the only
  // reactive value it reads; the rest are build-time constants.
  const checkVersion = useCallback(async () => {
    if (!enabled) return

    try {
      const response = await fetch(`${AGENT_URL}/version`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
      })

      if (!response.ok) {
        console.warn('Version check failed:', response.status)
        return
      }

      const data = await response.json()
      const serverVersion = data.version

      setLatestVersion(serverVersion)

      console.log('Version check:', {
        clientVersion: APP_VERSION,
        serverVersion,
        serverCommit: data.commit,
        match: APP_VERSION === serverVersion,
      })

      // Check if versions are different (simple string comparison)
      // Don't show notifications in development
      if (ENV !== 'development' && serverVersion !== APP_VERSION) {
        setNewVersionAvailable(true)
        // The once-per-page-life guard lives in promptReload, shared with
        // lazyWithReload — a stale chunk and this poll must not stack two
        // identical toasts.
        promptReload(
          'A new version of the app is available. Reload to get the latest features.'
        )
      } else {
        setNewVersionAvailable(false)
      }
    } catch (error) {
      console.warn('Version check error:', error)
    }
  }, [enabled])

  useEffect(() => {
    setCurrentVersion(APP_VERSION)

    if (!enabled) return

    // Check immediately
    checkVersion()

    // Set up periodic checking
    intervalRef.current = setInterval(checkVersion, checkInterval)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [enabled, checkInterval, checkVersion])

  // Clears the banner state only. The toast is already once-per-page-life.
  const dismissNotification = () => {
    setNewVersionAvailable(false)
  }

  const forceRefresh = () => {
    window.location.reload()
  }

  return {
    newVersionAvailable,
    currentVersion,
    latestVersion,
    dismissNotification,
    forceRefresh,
    checkVersion,
  }
}
